import type { EventCollectionItem } from '#types/components/cms/event-collection-item'

/**
 * Geographic point used to compare event proximity to the shopper.
 * @category Utils
 */
export type EventCoordinates = {
  latitude: number
  longitude: number
}

const toCoordinate = (value: unknown): number | null => {
  if (value === '' || value == null) return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const toCoordinates = (latitude: unknown, longitude: unknown): EventCoordinates | null => {
  const lat = toCoordinate(latitude)
  const lon = toCoordinate(longitude)
  if (lat == null || lon == null) return null
  return { latitude: lat, longitude: lon }
}

/**
 * Maps locale country codes onto event location codes. CMS uses `UK` where the locale uses `GB`.
 * @param countryCode - Locale or event country code.
 * @returns Uppercase code, or `''` when missing.
 * @category Utils
 */
export const normalizeEventCountryCode = (countryCode?: string | null) => {
  const normalized = countryCode?.trim().toUpperCase()
  if (!normalized) return ''
  return normalized === 'GB' ? 'UK' : normalized
}

/**
 * Parses an Edgescape `lat,lon` cookie value into numeric coordinates.
 * @param value - Raw `Edgescape-Lat-Long` cookie (`45.05,7.67`).
 * @returns Coordinates when both numbers are finite; otherwise `null`.
 * @category Utils
 */
export const parseEdgescapeLatLong = (value?: string | null): EventCoordinates | null => {
  if (!value) return null
  const [rawLat, rawLon] = value.split(',')
  return toCoordinates(rawLat?.trim(), rawLon?.trim())
}

/**
 * Picks shopper coordinates: browser geolocation first, then Edgescape if the cookie is present.
 * @param input - Candidate sources.
 * @param input.browser - Browser Geolocation coords. Non-finite values (VueUse's unset `Infinity`) are ignored.
 * @param input.cookie - Raw `Edgescape-Lat-Long` cookie.
 * @returns The first usable coordinates, or `null` when neither source is present.
 * @category Utils
 */
export const resolveUserCoordinates = ({
  browser,
  cookie,
}: {
  browser?: { latitude?: unknown, longitude?: unknown } | null
  cookie?: string | null
}): EventCoordinates | null =>
  toCoordinates(browser?.latitude, browser?.longitude) ?? parseEdgescapeLatLong(cookie)

/**
 * Reads an event's coordinates from its first location, falling back to event-level lat/long.
 * @param event - Event collection item.
 * @returns Coordinates when both values are finite; otherwise `null`.
 * @category Utils
 */
export const getEventCoordinates = (event: EventCollectionItem): EventCoordinates | null => {
  const location = event.locations?.[0]
  return toCoordinates(
    location?.latitude ?? event.latitude,
    location?.longitude ?? event.longitude,
  )
}

const getLocationCountryCodes = (event: EventCollectionItem) =>
  event.locations
    ?.map((location) => normalizeEventCountryCode(location?.countryCode))
    .filter(Boolean) ?? []

const getSquaredDistance = (origin: EventCoordinates, target: EventCoordinates) => {
  const dLat = target.latitude - origin.latitude
  const dLon = target.longitude - origin.longitude
  return dLat * dLat + dLon * dLon
}

const compareMissingLast = <T>(
  aValue: T | null | undefined,
  bValue: T | null | undefined,
  compare: (a: T, b: T) => number,
) => {
  if (!aValue && !bValue) return 0
  if (!aValue) return 1
  if (!bValue) return -1
  return compare(aValue, bValue)
}

/**
 * Sorts events so the closest to `origin` comes first. Events without coordinates stay last.
 * @param events - Events to order.
 * @param origin - Shopper coordinates from the browser or Edgescape.
 * @returns A new array; the input is not mutated.
 * @category Utils
 */
export const sortEventsByProximity = (
  events: EventCollectionItem[],
  origin: EventCoordinates,
) => [...events].sort((a, b) => compareMissingLast(
  getEventCoordinates(a),
  getEventCoordinates(b),
  (aCoords, bCoords) => getSquaredDistance(origin, aCoords) - getSquaredDistance(origin, bCoords),
))

const localeCountrySortRank = (event: EventCollectionItem, localeCountryCode?: string | null) => {
  const countryCodes = getLocationCountryCodes(event)
  if (!countryCodes.length) return 2

  const localeCountry = normalizeEventCountryCode(localeCountryCode)
  return localeCountry && countryCodes.includes(localeCountry) ? 0 : 1
}

/**
 * Sorts events whose location `countryCode` matches the locale country first.
 * Other events with a country follow; events with no country code stay last.
 * @param events - Events to order.
 * @param localeCountryCode - Country from the active locale.
 * @returns A new array; the input is not mutated.
 * @category Utils
 */
export const sortEventsByLocaleCountry = (
  events: EventCollectionItem[],
  localeCountryCode?: string | null,
) => [...events].sort((a, b) =>
  localeCountrySortRank(a, localeCountryCode) - localeCountrySortRank(b, localeCountryCode)
)
